import { v4 as uuidv4 } from 'uuid'

import { Organization } from '@modules-deprecated/app/organization/types/organization'
import { User } from '@modules-deprecated/app/user'

import { parsePayNowOAuthCallbackParams } from './parsePayNowOAuthCallback'

// Standard Connect OAuth. `response_type=code` + explicit `scope=read_write` are baked into the base
// URL — Stripe defaults Standard accounts to `read_only`, which would silently under-permission PayNow.
const STRIPE_OAUTH_URL = 'https://connect.stripe.com/oauth/authorize?response_type=code&scope=read_write'
const CSRF_TOKEN_KEY = 'billy-paynow-oauth-csrf'
// These sessionStorage keys are duplicated in apps/ember-webapp/app/routes/stripe-oauth-callback.js
// (Ember can't import this ES module). Keep the string values in sync across both files.
export const PAYNOW_OAUTH_RETURN_URL_KEY = 'billy-paynow-oauth-return-url'
export const PAYNOW_OAUTH_PENDING_KEY = 'billy-paynow-oauth-pending'
export const PAYNOW_STRIPE_OAUTH_CALLBACK_PATH = '/stripe_oauth_callback'
const payNowOAuthQueryParams = ['code', 'scope', 'state', 'error', 'error_description']

interface BuildPayNowOAuthUrlOptions {
  organization?: Organization
  redirectPath: string
  returnUrl?: string
  user?: User
}

interface NameParts {
  firstName: string
  lastName: string
}

const getUserNameParts = (user: User | undefined): NameParts => {
  const [firstName = '', ...rest] = user?.name?.trim().split(/\s+/) ?? []

  return {
    firstName,
    lastName: rest.join(' '),
  }
}

const addParam = (key: string, value: string | undefined) => {
  if (!value) {
    return ''
  }

  return `&${key}=${encodeURIComponent(value)}`
}

export const setPayNowOAuthReturnUrl = (returnUrl: string) => {
  sessionStorage.setItem(PAYNOW_OAUTH_RETURN_URL_KEY, returnUrl)
}

// Rebuilds a query string from the stored inner payload. The keys here are Stripe's raw wire names
// (`code`, `scope`, `state`, `error`, `error_description`) written by apps/ember-webapp/app/routes/
// stripe-oauth-callback.js — NOT the camelCase TS field names. They must stay as-is so the result can
// be re-parsed by `parsePayNowOAuthCallbackParams`. Do not swap in the TS field names (e.g. `errorDescription`).
const pendingParamsToSearch = (record: Record<string, unknown>): string => {
  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(record)) {
    if (typeof value === 'string' && value) {
      params.set(key, value)
    }
  }

  return `?${params.toString()}`
}

export const consumePendingPayNowOAuthCallbackSearch = (): string | undefined => {
  const pending = sessionStorage.getItem(PAYNOW_OAUTH_PENDING_KEY)

  if (!pending) {
    return undefined
  }

  sessionStorage.removeItem(PAYNOW_OAUTH_PENDING_KEY)

  // Typed as raw records rather than `PayNowOAuthCallbackParams` on purpose: the stored payload holds
  // Stripe's snake_cased wire keys (`error_description`), which no longer match the camelCase TS fields.
  const parsed = JSON.parse(pending) as {
    authorizationFailure?: Record<string, unknown>
    authorizationSuccess?: Record<string, unknown>
  }
  const inner = parsed.authorizationSuccess ?? parsed.authorizationFailure

  return inner ? pendingParamsToSearch(inner) : undefined
}

const getHashQuerySearch = (): string | undefined => {
  const hashQuery = window.location.hash.split('?')[1]

  if (!hashQuery) {
    return undefined
  }

  const search = `?${hashQuery}`
  const parsedSearch = parsePayNowOAuthCallbackParams(search)

  if (!parsedSearch.authorizationFailure && !parsedSearch.authorizationSuccess) {
    return undefined
  }

  return search
}

export const getPayNowOAuthCallbackSearch = (): string | undefined => {
  const pendingSearch = consumePendingPayNowOAuthCallbackSearch()

  if (pendingSearch) {
    return pendingSearch
  }

  if (window.location.search) {
    const parsedSearch = parsePayNowOAuthCallbackParams(window.location.search)

    if (parsedSearch.authorizationFailure || parsedSearch.authorizationSuccess) {
      return window.location.search
    }
  }

  return getHashQuerySearch()
}

export const generatePayNowOAuthCsrfToken = (): string => {
  const token = uuidv4()
  sessionStorage.setItem(CSRF_TOKEN_KEY, token)

  return token
}

export const validateAndConsumePayNowOAuthCsrfToken = (token?: string): boolean => {
  // Always consume (clear) the stored token, even on a missing/failed check, so it never lingers.
  const savedToken = sessionStorage.getItem(CSRF_TOKEN_KEY)
  sessionStorage.removeItem(CSRF_TOKEN_KEY)

  if (!token) {
    return false
  }

  return token === savedToken
}

// We always send Stripe to the same-origin `/stripe_oauth_callback` and hand the params back via
// sessionStorage. Returning straight to the settings route drops `code`/`state`/`error`: Ember's
// settings `beforeModel` redirects into the hash router and those aren't registered settings query params.
export const getPayNowOAuthRedirectUri = (protocol: string, redirectHost: string, returnUrl: string): string => {
  setPayNowOAuthReturnUrl(returnUrl)

  return `${protocol}//${redirectHost}${PAYNOW_STRIPE_OAUTH_CALLBACK_PATH}`
}

export const buildPayNowOAuthUrl = ({
  organization,
  redirectPath,
  returnUrl,
  user,
}: BuildPayNowOAuthUrlOptions): string => {
  const { firstName, lastName } = getUserNameParts(user)
  const { protocol, host } = window.location
  // `support.`→`mit.` is a Billy-only rewrite; it is a no-op for Shine hosts (no `support.` prefix).
  const redirectHost = host.replace('support.', 'mit.')
  const oauthReturnUrl = returnUrl ?? `${protocol}//${redirectHost}${redirectPath}`

  let url = STRIPE_OAUTH_URL
  url += addParam('client_id', window.ENV?.ZERVANT_STRIPE_CLIENT_ID)
  url += addParam('redirect_uri', getPayNowOAuthRedirectUri(protocol, redirectHost, oauthReturnUrl))
  url += addParam('state', generatePayNowOAuthCsrfToken())
  url += addParam('always_prompt', 'true')
  url += addParam('stripe_user[email]', user?.email ?? organization?.email)
  url += addParam('stripe_user[url]', organization?.url)
  url += addParam('stripe_user[country]', organization?.countryId)
  url += addParam('stripe_user[business_name]', organization?.name)
  url += addParam('stripe_user[first_name]', firstName)
  url += addParam('stripe_user[last_name]', lastName)
  url += addParam('stripe_user[street_address]', organization?.street)
  url += addParam('stripe_user[city]', organization?.city)
  url += addParam('stripe_user[zip]', organization?.zipcode)

  return url
}

export const clearPayNowOAuthQueryParams = (): void => {
  const url = new URL(window.location.href)

  for (const param of payNowOAuthQueryParams) {
    url.searchParams.delete(param)
  }

  const [hashPath, hashQuery] = url.hash.split('?')

  if (hashQuery) {
    const hashParams = new URLSearchParams(hashQuery)

    for (const param of payNowOAuthQueryParams) {
      hashParams.delete(param)
    }

    const remainingHashQuery = hashParams.toString()
    url.hash = remainingHashQuery ? `${hashPath}?${remainingHashQuery}` : hashPath
  }

  window.history.replaceState(null, '', url.toString())
}
